import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import InputContainer from '../InputContainer';
import QuestionInput from './QuestionInput';
import QuestionsMenu from './QuestionsMenu';
import NewQuestionsNotification from './NewQuestionsNotification';
import QuestionList from './QuestionList';
import { find, isEqualWith, cloneDeep } from 'lodash';
//import StreamOfflinePanel from '../StreamOfflinePanel';

function questionsAreEqual(questions, displayQuestions) {
  if (questions.length !== displayQuestions.length) return false;

  for (const question of questions) {
    const displayQuestion = find(displayQuestions, ['QuestionId', question.QuestionId]);

    if (displayQuestion) return isEqualWith(question, displayQuestion, questionIsEqual);

    return false;
  }
}

// Needed because Question.TimeAsked could be slightly different between local state and server state
function questionIsEqual(q1, q2) {
  return (
    q1.QuestionId === q2.QuestionId &&
    q1.SessionId === q2.SessionId &&
    q1.Question === q2.Question &&
    q1.Answer === q2.Answer &&
    q1.UserId === q2.UserId &&
    q1.UserName === q2.UserName &&
    q1.Hidden === q2.Hidden
  );
}

export default function Questions() {
  const [sorted, setSorted] = useState('Recent');
  const [filtered, setFiltered] = useState('Show All');
  const [displayQuestions, setDisplayQuestions] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [scrolledToTop, setScrolledToTop] = useState(true);
  const [bypassNextScroll, setBypassNextScroll] = useState(false);
  const { userId, questions } = useSelector(({ user, conference }) => ({
    userId: user.userId,
    questions: conference.questions,
  }));

  useEffect(() => {
    if (scrolledToTop || bypassNextScroll) {
      setDisplayQuestions(questions);
      setBypassNextScroll(false);
    } else {
      const newDisplayQuestions = cloneDeep(displayQuestions);

      // Update these in real time since they don't change DOM sizes.
      for (const question of questions) {
        const displayQuestion = find(newDisplayQuestions, ['QuestionId', question.QuestionId]);
        if (displayQuestion) {
          displayQuestion.VoteCount = question.VoteCount;
          displayQuestion.TimeAsked = question.TimeAsked;
        }
      }

      setDisplayQuestions(newDisplayQuestions);

      setHasChanges(!isEqualWith(questions, displayQuestions, questionsAreEqual));
    }
  }, [questions, scrolledToTop]); // eslint-disable-line react-hooks/exhaustive-deps

  //if (status !== 'LIVE') return <StreamOfflinePanel status={status} userRole={userRole} />;

  const onScrolled = (scrollTop) => {
    if (scrollTop === 0) {
      setScrolledToTop(true);
      setHasChanges(false);
    } else {
      setScrolledToTop(false);
    }
  };

  const getUpdates = () => {
    setDisplayQuestions(questions);
    setHasChanges(false);
  };

  const bypassNextIfScrolled = () => {
    if (!scrolledToTop) setBypassNextScroll(true);
  };

  const questionsArray = displayQuestions
    .filter((q) => {
      if (q.Hidden) return false;
      switch (filtered) {
        case 'Answered':
          return !!q.Answer;
        case 'Not Answered':
          return !q.Answer;
        case 'My Questions':
          return q.UserId === userId;
        default:
          return true;
      }
    })
    .sort((a, b) => {
      const aTimeAsked = Number(a.TimeAsked);
      const bTimeAsked = Number(b.TimeAsked);

      if (sorted === 'Popular') {
        if (a.VoteCount !== b.VoteCount) return b.VoteCount - a.VoteCount;
      }

      if (sorted === 'Oldest') return aTimeAsked - bTimeAsked;

      return bTimeAsked - aTimeAsked;
    });

  return (
    <>
      <QuestionsMenu
        sorted={sorted}
        setSorted={setSorted}
        filtered={filtered}
        setFiltered={setFiltered}
      />

      <div style={{ flexGrow: 1 }}>
        <QuestionList
          questionsArray={questionsArray}
          getUpdates={getUpdates}
          bypassNextIfScrolled={bypassNextIfScrolled}
          onScrolled={onScrolled}
        />

        {hasChanges && <NewQuestionsNotification onClick={getUpdates} />}
      </div>

      <InputContainer>
        <QuestionInput bypassNextIfScrolled={bypassNextIfScrolled} getUpdates={getUpdates} />
      </InputContainer>
    </>
  );
}
